import { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import { 
  FileText, Phone, User, AlertTriangle, CheckCircle2, XCircle,
  Calendar, MapPin, Briefcase, RefreshCw, Loader2, Search,
  Download, Filter, TrendingUp, TrendingDown, Eye, Edit, Star,
  ArrowUpDown, ArrowUp, ArrowDown, FileAudio, Brain, BarChart3,
  PieChart, Activity, Target, Shield, MessageSquare, Check, ExternalLink,
  Clock, ChevronDown, Database, Play, History, Trash2, Timer
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  getCallLogNotes, 
  getCallLogComplianceStats, 
  getComplianceByUser,
  getComplianceAlerts,
  syncCallLogNotes,
  syncCallLogNotesFromCalls,
  syncCallLogNotesEnhanced,
  getCallLogSyncStatus,
  getCallLogSyncHistory,
  clearCallLogSyncHistory,
  seedCallLogDemo,
  getUsers,
  getDepartments,
  updateCallLogNote,
  reviewAlert,
  bulkReviewAlerts,
  overrideAlertCompliance
} from '@/lib/api';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { format, formatDistanceToNow } from 'date-fns';

const CLASSIFICATIONS = [
  { value: 'best', label: 'Best', color: 'bg-emerald-500' },
  { value: 'good', label: 'Good', color: 'bg-blue-500' },
  { value: 'improvement', label: 'Improvement', color: 'bg-amber-500' },
  { value: 'high_alert', label: 'High Alert', color: 'bg-red-500' },
];

const ALERT_LEVELS = [
  { value: 'good', label: 'Good', color: 'bg-emerald-500' },
  { value: 'improvement', label: 'Improvement', color: 'bg-yellow-500' },
  { value: 'medium', label: 'Medium', color: 'bg-amber-500' },
  { value: 'critical', label: 'Critical', color: 'bg-red-500' },
];

// Sortable header component
const SortableHeader = ({ label, sortKey, currentSort, onSort }) => {
  const isActive = currentSort.key === sortKey;
  const Icon = isActive ? (currentSort.direction === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  
  return (
    <button
      className="flex items-center gap-1 hover:text-slate-900 transition-colors font-medium"
      onClick={() => onSort(sortKey)}
    >
      {label}
      <Icon className={`h-4 w-4 ${isActive ? 'text-blue-600' : 'text-muted-foreground'}`} />
    </button>
  );
};

// Chart colors
const CHART_COLORS = {
  best: '#10b981',
  good: '#3b82f6', 
  improvement: '#f59e0b',
  high_alert: '#ef4444',
  critical: '#dc2626',
  medium: '#f97316',
  primary: '#8b5cf6',
  secondary: '#06b6d4'
};

// Analytics Tab Component
const AnalyticsTab = ({ callLogs, complianceByUser, complianceStats, users, departments }) => {
  const [timeRange, setTimeRange] = useState('this_month');
  const [selectedDept, setSelectedDept] = useState('all');
  
  // Calculate date range based on selection
  const getDateRange = () => {
    const now = new Date();
    let startDate, endDate = now;
    
    switch (timeRange) {
      case 'this_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'last_month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'last_3_months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        break;
      case 'last_6_months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
        break;
      case 'this_year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    }
    return { startDate, endDate };
  };
  
  // Filter logs by date range and department
  const filteredLogs = useMemo(() => {
    const now = new Date();
    let startDate, endDate = now;
    
    switch (timeRange) {
      case 'this_month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'last_month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'last_3_months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        break;
      case 'last_6_months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
        break;
      case 'this_year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    }
    
    return callLogs.filter(log => {
      const logDate = new Date(log.call_date);
      const inRange = logDate >= startDate && logDate <= endDate;
      const inDept = selectedDept === 'all' || log.department_id === selectedDept;
      return inRange && inDept;
    });
  }, [callLogs, timeRange, selectedDept]);
  
  // Calculate classification distribution
  const classificationData = useMemo(() => {
    const counts = { best: 0, good: 0, improvement: 0, high_alert: 0 };
    filteredLogs.forEach(log => {
      if (log.classification && counts[log.classification] !== undefined) {
        counts[log.classification]++;
      }
    });
    return Object.entries(counts).map(([name, value]) => ({
      name: name.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value,
      color: CHART_COLORS[name]
    })).filter(d => d.value > 0);
  }, [filteredLogs]);
  
  // Calculate alert level distribution
  const alertData = useMemo(() => {
    const counts = { good: 0, improvement: 0, medium: 0, critical: 0 };
    filteredLogs.forEach(log => {
      if (log.alert_level && counts[log.alert_level] !== undefined) {
        counts[log.alert_level]++;
      }
    });
    return Object.entries(counts).map(([name, value]) => ({
      name: name.replace(/\b\w/g, l => l.toUpperCase()),
      value,
      color: CHART_COLORS[name === 'good' ? 'best' : name]
    })).filter(d => d.value > 0);
  }, [filteredLogs]);
  
  // Calculate daily trend data
  const dailyTrendData = useMemo(() => {
    const dailyCounts = {};
    filteredLogs.forEach(log => {
      const date = new Date(log.call_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (!dailyCounts[date]) {
        dailyCounts[date] = { date, total: 0, compliant: 0, nonCompliant: 0 };
      }
      dailyCounts[date].total++;
      if (log.has_notes || log.manually_marked_compliant) {
        dailyCounts[date].compliant++;
      } else {
        dailyCounts[date].nonCompliant++;
      }
    });
    return Object.values(dailyCounts).slice(-14); // Last 14 days
  }, [filteredLogs]);
  
  // Calculate user performance data
  const userPerformanceData = useMemo(() => {
    return complianceByUser
      .filter(u => selectedDept === 'all' || u.department_id === selectedDept)
      .sort((a, b) => b.compliance_rate - a.compliance_rate)
      .slice(0, 10)
      .map(u => ({
        name: u.user_name?.split(' ')[0] || 'Unknown',
        fullName: u.user_name,
        compliance: u.compliance_rate,
        total: u.total_calls,
        withNotes: u.calls_with_notes
      }));
  }, [complianceByUser, selectedDept]);
  
  // Calculate department comparison
  const departmentData = useMemo(() => {
    const deptStats = {};
    filteredLogs.forEach(log => {
      const deptId = log.department_id || 'unknown';
      if (!deptStats[deptId]) {
        deptStats[deptId] = { total: 0, compliant: 0 };
      }
      deptStats[deptId].total++;
      if (log.has_notes || log.manually_marked_compliant) {
        deptStats[deptId].compliant++;
      }
    });
    
    return departments.map(dept => ({
      name: dept.name,
      total: deptStats[dept.id]?.total || 0,
      compliance: deptStats[dept.id] ? 
        Math.round((deptStats[dept.id].compliant / deptStats[dept.id].total) * 100) : 0
    })).filter(d => d.total > 0);
  }, [filteredLogs, departments]);

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Time Range:</span>
            </div>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-[180px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="this_month">This Month</SelectItem>
                <SelectItem value="last_month">Last Month</SelectItem>
                <SelectItem value="last_3_months">Last 3 Months</SelectItem>
                <SelectItem value="last_6_months">Last 6 Months</SelectItem>
                <SelectItem value="this_year">This Year</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={selectedDept} onValueChange={setSelectedDept}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept.id} value={dept.id}>{dept.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Badge variant="outline" className="ml-auto">
              {filteredLogs.length} calls in range
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 dark:from-purple-500/20 dark:to-purple-600/10 border-purple-200 dark:border-purple-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Calls</p>
                <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">{filteredLogs.length}</p>
              </div>
              <Phone className="h-8 w-8 text-purple-400 dark:text-purple-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 dark:from-emerald-500/20 dark:to-emerald-600/10 border-emerald-200 dark:border-emerald-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Compliant</p>
                <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                  {filteredLogs.filter(l => l.has_notes || l.manually_marked_compliant).length}
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-emerald-400 dark:text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 dark:from-red-500/20 dark:to-red-600/10 border-red-200 dark:border-red-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Non-Compliant</p>
                <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                  {filteredLogs.filter(l => !l.has_notes && !l.manually_marked_compliant).length}
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-400 dark:text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 dark:from-blue-500/20 dark:to-blue-600/10 border-blue-200 dark:border-blue-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Compliance Rate</p>
                <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                  {filteredLogs.length > 0 
                    ? Math.round((filteredLogs.filter(l => l.has_notes || l.manually_marked_compliant).length / filteredLogs.length) * 100)
                    : 0}%
                </p>
              </div>
              <Target className="h-8 w-8 text-blue-400 dark:text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Trend Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-purple-600" />
              Daily Compliance Trend
            </CardTitle>
            <CardDescription>Compliant vs Non-compliant calls over time</CardDescription>
          </CardHeader>
          <CardContent>
            {dailyTrendData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={dailyTrendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="compliant" stackId="1" stroke="#10b981" fill="#10b981" fillOpacity={0.6} name="Compliant" />
                  <Area type="monotone" dataKey="nonCompliant" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6} name="Non-Compliant" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                No data available for selected range
              </div>
            )}
          </CardContent>
        </Card>

        {/* Classification Pie Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <PieChart className="h-5 w-5 text-blue-600" />
              Call Classification Distribution
            </CardTitle>
            <CardDescription>Breakdown by call quality classification</CardDescription>
          </CardHeader>
          <CardContent>
            {classificationData.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <RechartsPie>
                  <Pie
                    data={classificationData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {classificationData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </RechartsPie>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-muted-foreground">
                No classification data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Performance Bar Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-emerald-600" />
              Top Performing Users
            </CardTitle>
            <CardDescription>Users ranked by compliance rate</CardDescription>
          </CardHeader>
          <CardContent>
            {userPerformanceData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={userPerformanceData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <Tooltip 
                    formatter={(value, name, props) => [
                      `${value}%`,
                      `Compliance (${props.payload.withNotes}/${props.payload.total} calls)`
                    ]}
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="compliance" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No user data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Department Comparison */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-amber-600" />
              Department Comparison
            </CardTitle>
            <CardDescription>Compliance rate by department</CardDescription>
          </CardHeader>
          <CardContent>
            {departmentData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={departmentData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} stroke="#94a3b8" />
                  <Tooltip 
                    formatter={(value, name, props) => [
                      name === 'compliance' ? `${value}%` : value,
                      name === 'compliance' ? 'Compliance Rate' : 'Total Calls'
                    ]}
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="compliance" fill="#f59e0b" name="Compliance %" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="total" fill="#06b6d4" name="Total Calls" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                No department data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Alert Level Distribution */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            Alert Level Distribution
          </CardTitle>
          <CardDescription>Breakdown of calls by alert severity</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {alertData.map((alert, i) => (
              <div 
                key={i} 
                className="p-4 rounded-lg text-center"
                style={{ backgroundColor: `${alert.color}15` }}
              >
                <p className="text-3xl font-bold" style={{ color: alert.color }}>
                  {alert.value}
                </p>
                <p className="text-sm text-muted-foreground">{alert.name}</p>
                <p className="text-xs text-muted-foreground">
                  {filteredLogs.length > 0 ? Math.round((alert.value / filteredLogs.length) * 100) : 0}%
                </p>
              </div>
            ))}
            {alertData.length === 0 && (
              <div className="col-span-4 text-center py-8 text-muted-foreground">
                No alert data available
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default function NotesCompliance() {
  // Handle both legacy admin and SaaS tenant contexts
  const outletContext = useOutletContext() || {};
  const { users } = outletContext;
  const [activeTab, setActiveTab] = useState('call-logs');
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  
  // Sync status state
  const [syncStatus, setSyncStatus] = useState({
    last_sync_at: null,
    last_sync_type: null,
    last_sync_count: 0,
    is_syncing: false,
    sync_progress: 0,
    sync_message: null,
    total_notes: 0
  });
  const [showSyncDatePicker, setShowSyncDatePicker] = useState(false);
  const [syncFromDate, setSyncFromDate] = useState(null);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const autoRefreshInterval = useRef(null);
  
  // Call Log Notes data
  const [callLogs, setCallLogs] = useState([]);
  const [complianceStats, setComplianceStats] = useState(null);
  const [complianceByUser, setComplianceByUser] = useState([]);
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [departments, setDepartments] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    user_id: 'all',
    user_name: '',
    location: 'all',
    designation: 'all',
    has_notes: 'all',
    date_from: '',
    date_to: ''
  });
  
  // Detail view
  const [selectedLog, setSelectedLog] = useState(null);
  const [showTranscript, setShowTranscript] = useState(null);
  const [showNotes, setShowNotes] = useState(null);
  const [editingLog, setEditingLog] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  
  // Bulk selection state
  const [selectedLogs, setSelectedLogs] = useState([]);
  const [showBulkActionsDialog, setShowBulkActionsDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState('review'); // 'review' or 'mark_compliant'
  const [bulkQaNotes, setBulkQaNotes] = useState('');
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'call_date', direction: 'desc' });
  
  // Sync history state
  const [showSyncHistory, setShowSyncHistory] = useState(false);
  const [syncHistory, setSyncHistory] = useState([]);
  const [syncHistorySummary, setSyncHistorySummary] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  
  // Get unique locations and designations from users
  const locations = [...new Set(users?.filter(u => u.location).map(u => u.location) || [])];
  const designations = [...new Set(users?.filter(u => u.designation).map(u => u.designation) || [])];
  
  // Load sync history
  const loadSyncHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const res = await getCallLogSyncHistory({ limit: 50 });
      setSyncHistory(res.data.history);
      setSyncHistorySummary(res.data.summary);
    } catch (error) {
      console.error('Failed to load sync history:', error);
      toast.error('Failed to load sync history');
    } finally {
      setIsLoadingHistory(false);
    }
  };
  
  // Clear old sync history
  const handleClearHistory = async (days = 30) => {
    try {
      const res = await clearCallLogSyncHistory(days);
      toast.success(res.data.message);
      loadSyncHistory();
    } catch (error) {
      toast.error('Failed to clear sync history');
    }
  };
  
  // Load sync status
  const loadSyncStatus = async () => {
    try {
      const res = await getCallLogSyncStatus();
      setSyncStatus(res.data);
      return res.data;
    } catch (error) {
      console.error('Failed to load sync status:', error);
    }
  };
  
  // Auto-refresh effect
  useEffect(() => {
    if (autoRefreshEnabled) {
      // Check for new calls every 30 seconds
      autoRefreshInterval.current = setInterval(() => {
        loadSyncStatus().then(status => {
          // If not currently syncing and there might be new data, reload
          if (!status?.is_syncing) {
            loadCallLogs();
            loadData();
          }
        });
      }, 30000);
    } else {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
      }
    }
    
    return () => {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
      }
    };
  }, [autoRefreshEnabled]);
  
  // Poll for sync status when syncing
  useEffect(() => {
    let pollInterval;
    if (syncStatus.is_syncing) {
      pollInterval = setInterval(async () => {
        const status = await loadSyncStatus();
        if (status && !status.is_syncing) {
          // Sync completed, reload data
          toast.success(`Sync completed: ${status.last_sync_count} calls synced`);
          loadData();
          loadCallLogs();
        }
      }, 2000);
    }
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [syncStatus.is_syncing]);

  useEffect(() => {
    loadData();
    loadSyncStatus();
  }, []);

  useEffect(() => {
    if (activeTab === 'call-logs') {
      loadCallLogs();
    }
  }, [filters, activeTab]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const [statsRes, byUserRes, alertsRes, deptRes] = await Promise.all([
        getCallLogComplianceStats({}),
        getComplianceByUser(),
        getComplianceAlerts(),
        getDepartments()
      ]);
      
      setComplianceStats(statsRes.data);
      setComplianceByUser(byUserRes.data);
      setComplianceAlerts(alertsRes.data);
      setDepartments(deptRes.data);
      
      await loadCallLogs();
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadCallLogs = async () => {
    try {
      const params = {};
      if (filters.user_id !== 'all') params.user_id = filters.user_id;
      if (filters.user_name) params.user_name = filters.user_name;
      if (filters.location !== 'all') params.location = filters.location;
      if (filters.designation !== 'all') params.designation = filters.designation;
      if (filters.has_notes !== 'all') params.has_notes = filters.has_notes === 'yes';
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      
      const res = await getCallLogNotes(params);
      setCallLogs(res.data);
    } catch (error) {
      console.error('Failed to load call logs:', error);
    }
  };

  // Enhanced sync handlers
  const handleEnhancedSync = async (syncType, fromDate = null) => {
    setIsSyncing(true);
    try {
      const options = { sync_type: syncType };
      if (fromDate) {
        options.from_date = fromDate;
      }
      await syncCallLogNotesEnhanced(options);
      toast.success(`Sync started (${syncType}). Watching for progress...`);
      loadSyncStatus();
    } catch (error) {
      toast.error('Failed to start sync');
      setIsSyncing(false);
    }
  };

  const handleSyncAll = () => handleEnhancedSync('all');
  const handleSyncDelta = () => handleEnhancedSync('delta');
  const handleSyncFromDate = () => {
    if (syncFromDate) {
      handleEnhancedSync('from_date', syncFromDate.toISOString());
      setShowSyncDatePicker(false);
      setSyncFromDate(null);
    } else {
      toast.error('Please select a date first');
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await syncCallLogNotes();
      toast.success('Sync started. Data will be updated shortly.');
      setTimeout(loadData, 3000);
    } catch (error) {
      toast.error('Failed to start sync');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSyncFromCalls = async () => {
    setIsSyncing(true);
    try {
      const res = await syncCallLogNotesFromCalls();
      toast.success(`Synced ${res.data.synced_count} calls to reports`);
      loadData();
    } catch (error) {
      toast.error('Failed to sync from calls');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleSeedDemo = async () => {
    setIsSeeding(true);
    try {
      const res = await seedCallLogDemo();
      toast.success(`Demo data seeded: ${res.data.total_logs} call logs created`);
      loadData();
    } catch (error) {
      toast.error('Failed to seed demo data');
    } finally {
      setIsSeeding(false);
    }
  };

  const exportToCSV = () => {
    const headers = ['Date', 'Time', 'Created By', 'Applicant', 'Phone', 'Call Type', 'Duration', 'Classification', 'Alert Level', 'Has Notes', 'Notes'];
    const rows = sortedCallLogs.map(log => [
      new Date(log.call_date).toLocaleDateString(),
      new Date(log.call_date).toLocaleTimeString(),
      log.log_created_by,
      log.applicant_name || '-',
      log.phone_number,
      log.call_type,
      log.duration || '-',
      log.classification || '-',
      log.alert_level || '-',
      log.has_notes ? 'Yes' : 'No',
      `"${(log.notes || '').replace(/"/g, '""')}"`
    ]);
    
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `call-log-notes-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort call logs
  const sortedCallLogs = useMemo(() => {
    if (!callLogs.length) return [];
    
    return [...callLogs].sort((a, b) => {
      const { key, direction } = sortConfig;
      let aVal, bVal;
      
      switch (key) {
        case 'call_date':
          aVal = new Date(a.call_date).getTime();
          bVal = new Date(b.call_date).getTime();
          break;
        case 'log_created_by':
          aVal = (a.log_created_by || '').toLowerCase();
          bVal = (b.log_created_by || '').toLowerCase();
          break;
        case 'applicant_name':
          aVal = (a.applicant_name || '').toLowerCase();
          bVal = (b.applicant_name || '').toLowerCase();
          break;
        case 'phone_number':
          aVal = a.phone_number || '';
          bVal = b.phone_number || '';
          break;
        case 'call_type':
          aVal = (a.call_type || '').toLowerCase();
          bVal = (b.call_type || '').toLowerCase();
          break;
        case 'duration':
          // Parse duration like "1:02" to seconds
          const parseDuration = (d) => {
            if (!d) return 0;
            const parts = d.split(':');
            return parseInt(parts[0]) * 60 + parseInt(parts[1] || 0);
          };
          aVal = parseDuration(a.duration);
          bVal = parseDuration(b.duration);
          break;
        case 'classification':
          const classOrder = { best: 0, good: 1, improvement: 2, high_alert: 3 };
          aVal = classOrder[a.classification] ?? 99;
          bVal = classOrder[b.classification] ?? 99;
          break;
        case 'alert_level':
          const alertOrder = { good: 0, improvement: 1, medium: 2, critical: 3 };
          aVal = alertOrder[a.alert_level] ?? 99;
          bVal = alertOrder[b.alert_level] ?? 99;
          break;
        case 'has_notes':
          aVal = a.has_notes || a.manually_marked_compliant ? 1 : 0;
          bVal = b.has_notes || b.manually_marked_compliant ? 1 : 0;
          break;
        default:
          aVal = a[key] || '';
          bVal = b[key] || '';
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [callLogs, sortConfig]);

  const handleSaveEdit = async () => {
    if (!editingLog) return;
    
    setIsSaving(true);
    try {
      await updateCallLogNote(editingLog.id, {
        has_notes: editingLog.has_notes,
        manually_marked_compliant: editingLog.manually_marked_compliant,
        admin_notes: editingLog.admin_notes,
        classification: editingLog.classification,
        alert_level: editingLog.alert_level
      });
      toast.success('Call log updated successfully');
      setEditingLog(null);
      loadCallLogs();
      loadData(); // Refresh stats
    } catch (error) {
      toast.error('Failed to update call log');
    } finally {
      setIsSaving(false);
    }
  };

  // Bulk selection handlers
  const handleSelectLog = (logId) => {
    setSelectedLogs(prev => 
      prev.includes(logId) 
        ? prev.filter(id => id !== logId) 
        : [...prev, logId]
    );
  };

  const handleSelectAll = () => {
    if (selectedLogs.length === sortedCallLogs.length) {
      setSelectedLogs([]);
    } else {
      setSelectedLogs(sortedCallLogs.map(log => log.id));
    }
  };

  const handleBulkAction = async () => {
    if (selectedLogs.length === 0) return;
    
    setIsBulkProcessing(true);
    try {
      if (bulkAction === 'review') {
        // Mark selected logs as reviewed
        await bulkReviewAlerts(selectedLogs, bulkQaNotes || null);
        toast.success(`${selectedLogs.length} call(s) marked as reviewed`);
      } else if (bulkAction === 'mark_compliant') {
        // Mark selected logs as manually compliant
        const updatePromises = selectedLogs.map(logId => 
          updateCallLogNote(logId, {
            manually_marked_compliant: true,
            admin_notes: bulkQaNotes || 'Bulk marked as compliant by QA'
          })
        );
        await Promise.all(updatePromises);
        toast.success(`${selectedLogs.length} call(s) marked as compliant`);
      } else if (bulkAction === 'mark_non_compliant') {
        // Mark selected logs as non-compliant
        const updatePromises = selectedLogs.map(logId => 
          updateCallLogNote(logId, {
            manually_marked_compliant: false,
            has_notes: false,
            admin_notes: bulkQaNotes || 'Bulk marked as non-compliant by QA'
          })
        );
        await Promise.all(updatePromises);
        toast.success(`${selectedLogs.length} call(s) marked as non-compliant`);
      }
      
      setSelectedLogs([]);
      setBulkQaNotes('');
      setShowBulkActionsDialog(false);
      loadCallLogs();
      loadData();
    } catch (error) {
      console.error('Bulk action error:', error);
      toast.error('Failed to process bulk action');
    } finally {
      setIsBulkProcessing(false);
    }
  };

  const handleSingleReview = async (log, qaNotes) => {
    try {
      await reviewAlert(log.id, { qa_notes: qaNotes });
      toast.success('Call marked as reviewed');
      loadCallLogs();
      loadData();
    } catch (error) {
      toast.error('Failed to mark as reviewed');
    }
  };

  const handleSingleCompliance = async (log, isCompliant, qaNotes) => {
    try {
      await updateCallLogNote(log.id, {
        manually_marked_compliant: isCompliant,
        admin_notes: qaNotes
      });
      toast.success(`Call marked as ${isCompliant ? 'compliant' : 'non-compliant'}`);
      loadCallLogs();
      loadData();
    } catch (error) {
      toast.error('Failed to update compliance status');
    }
  };

  const startEditingLog = (log) => {
    setEditingLog({ ...log });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notes-compliance-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-6 text-white">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 rounded-lg">
              <FileText className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Notes Compliance</h1>
              <p className="text-purple-100 mt-1">
                Call log notes compliance and analytics
              </p>
            </div>
          </div>
          
          {/* Sync Status & Controls */}
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3">
            {/* Last Sync Info */}
            <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2">
              <Clock className="h-4 w-4" />
              <div className="text-sm">
                <span className="opacity-80">Last sync:</span>{' '}
                <span className="font-medium">
                  {syncStatus.last_sync_at 
                    ? format(new Date(syncStatus.last_sync_at), 'MMM d, h:mm a')
                    : 'Never'}
                </span>
                {syncStatus.last_sync_type && (
                  <Badge variant="secondary" className="ml-2 text-xs bg-white/20 text-white">
                    {syncStatus.last_sync_type}
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Auto-refresh toggle */}
            <div className="flex items-center gap-2 bg-white/10 rounded-lg px-3 py-2">
              <RefreshCw className={`h-4 w-4 ${autoRefreshEnabled ? 'animate-spin' : ''}`} />
              <span className="text-sm">Auto-refresh</span>
              <Switch
                checked={autoRefreshEnabled}
                onCheckedChange={setAutoRefreshEnabled}
                className="data-[state=checked]:bg-white/30"
              />
            </div>
            
            {/* Sync Progress (shown when syncing) */}
            {syncStatus.is_syncing && (
              <div className="flex items-center gap-2 bg-amber-500/20 rounded-lg px-3 py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <div className="text-sm">
                  <span>{syncStatus.sync_message || 'Syncing...'}</span>
                  <Progress value={syncStatus.sync_progress} className="w-24 h-1 mt-1" />
                </div>
              </div>
            )}
            
            {/* Sync Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  disabled={isSyncing || syncStatus.is_syncing}
                  className="bg-white text-purple-600 hover:bg-white/90"
                  data-testid="sync-dropdown-btn"
                >
                  {(isSyncing || syncStatus.is_syncing) ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Database className="h-4 w-4 mr-2" />
                  )}
                  Sync Data
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <DropdownMenuItem onClick={handleSyncAll} data-testid="sync-all-btn">
                  <RefreshCw className="h-4 w-4 mr-2" />
                  <div>
                    <div className="font-medium">Sync All Calls</div>
                    <div className="text-xs text-muted-foreground">Re-sync all calls from database</div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={handleSyncDelta} 
                  disabled={!syncStatus.last_sync_at}
                  data-testid="sync-delta-btn"
                >
                  <Play className="h-4 w-4 mr-2" />
                  <div>
                    <div className="font-medium">Sync Since Last</div>
                    <div className="text-xs text-muted-foreground">
                      {syncStatus.last_sync_at 
                        ? `From ${format(new Date(syncStatus.last_sync_at), 'MMM d, yyyy')}`
                        : 'No previous sync'}
                    </div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <div className="p-2">
                  <div className="text-sm font-medium mb-2">Sync from specific date:</div>
                  <Popover open={showSyncDatePicker} onOpenChange={setShowSyncDatePicker}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <Calendar className="mr-2 h-4 w-4" />
                        {syncFromDate ? format(syncFromDate, 'PPP') : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="end">
                      <CalendarComponent
                        mode="single"
                        selected={syncFromDate}
                        onSelect={setSyncFromDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                  {syncFromDate && (
                    <Button 
                      size="sm" 
                      className="w-full mt-2"
                      onClick={handleSyncFromDate}
                      data-testid="sync-from-date-btn"
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Sync from {format(syncFromDate, 'MMM d')}
                    </Button>
                  )}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleSync} data-testid="sync-ceipal-btn">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  <div>
                    <div className="font-medium">Sync from CEIPAL API</div>
                    <div className="text-xs text-muted-foreground">Pull data from external API</div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Sync History Button */}
            <Dialog open={showSyncHistory} onOpenChange={setShowSyncHistory}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                  onClick={() => {
                    setShowSyncHistory(true);
                    loadSyncHistory();
                  }}
                  data-testid="sync-history-btn"
                >
                  <History className="h-4 w-4 mr-2" />
                  History
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Sync History
                  </DialogTitle>
                  <DialogDescription>
                    Track all synchronization operations with timestamps, counts, and status.
                  </DialogDescription>
                </DialogHeader>
                
                {/* Summary Stats */}
                {syncHistorySummary && (
                  <div className="grid grid-cols-5 gap-3 py-3 border-b">
                    <div className="text-center">
                      <p className="text-2xl font-bold">{syncHistorySummary.total_syncs}</p>
                      <p className="text-xs text-muted-foreground">Total Syncs</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-600">{syncHistorySummary.completed}</p>
                      <p className="text-xs text-muted-foreground">Completed</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-red-600">{syncHistorySummary.failed}</p>
                      <p className="text-xs text-muted-foreground">Failed</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-blue-600">{syncHistorySummary.total_calls_synced?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Calls Synced</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-purple-600">{syncHistorySummary.avg_duration_seconds}s</p>
                      <p className="text-xs text-muted-foreground">Avg Duration</p>
                    </div>
                  </div>
                )}
                
                {/* History Table */}
                <div className="flex-1 overflow-auto">
                  {isLoadingHistory ? (
                    <div className="flex items-center justify-center h-48">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : syncHistory.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-48 text-muted-foreground">
                      <History className="h-12 w-12 mb-2 opacity-50" />
                      <p>No sync history yet</p>
                      <p className="text-sm">Run a sync to see history here</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Time</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Calls</TableHead>
                          <TableHead className="text-right">Duration</TableHead>
                          <TableHead>From Date</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {syncHistory.map((record) => (
                          <TableRow key={record.id} className={record.status === 'failed' ? 'bg-red-50' : ''}>
                            <TableCell className="whitespace-nowrap">
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {record.started_at ? format(new Date(record.started_at), 'MMM d, h:mm a') : '-'}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {record.started_at ? formatDistanceToNow(new Date(record.started_at), { addSuffix: true }) : ''}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">
                                {record.sync_type === 'from_date' ? 'Custom Date' : record.sync_type}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {record.status === 'completed' ? (
                                <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  Completed
                                </Badge>
                              ) : (
                                <Badge variant="destructive" className="bg-red-100 text-red-700 hover:bg-red-100">
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Failed
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {record.calls_synced?.toLocaleString() || 0}
                              {record.total_calls_found > 0 && (
                                <span className="text-muted-foreground text-xs ml-1">
                                  / {record.total_calls_found}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1 text-muted-foreground">
                                <Timer className="h-3 w-3" />
                                {record.duration_seconds ? `${record.duration_seconds}s` : '-'}
                              </div>
                            </TableCell>
                            <TableCell>
                              {record.from_date ? (
                                <span className="text-sm">
                                  {format(new Date(record.from_date), 'MMM d, yyyy')}
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-sm">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
                
                <DialogFooter className="border-t pt-4">
                  <div className="flex items-center justify-between w-full">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Trash2 className="h-4 w-4 mr-2" />
                          Clear Old History
                          <ChevronDown className="h-4 w-4 ml-2" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => handleClearHistory(7)}>
                          Older than 7 days
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleClearHistory(14)}>
                          Older than 14 days
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleClearHistory(30)}>
                          Older than 30 days
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button variant="outline" onClick={() => loadSyncHistory()}>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Compliance Stats */}
      {complianceStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Calls</p>
                  <p className="text-3xl font-bold">{complianceStats.total_calls}</p>
                </div>
                <Phone className="h-8 w-8 text-muted-foreground/70" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">With Notes</p>
                  <p className="text-3xl font-bold text-emerald-600">{complianceStats.calls_with_notes}</p>
                </div>
                <CheckCircle2 className="h-8 w-8 text-emerald-300" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Missing Notes</p>
                  <p className="text-3xl font-bold text-red-600">{complianceStats.calls_without_notes}</p>
                </div>
                <XCircle className="h-8 w-8 text-red-300" />
              </div>
            </CardContent>
          </Card>
          
          <Card className={complianceStats.compliance_rate >= 80 ? 'border-emerald-200' : 'border-red-200'}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">Compliance Rate</p>
                {complianceStats.compliance_rate >= 80 ? (
                  <TrendingUp className="h-5 w-5 text-emerald-500" />
                ) : (
                  <TrendingDown className="h-5 w-5 text-red-500" />
                )}
              </div>
              <p className={`text-3xl font-bold ${complianceStats.compliance_rate >= 80 ? 'text-emerald-600' : 'text-red-600'}`}>
                {complianceStats.compliance_rate}%
              </p>
              <Progress 
                value={complianceStats.compliance_rate} 
                className={`mt-2 h-2 ${complianceStats.compliance_rate >= 80 ? '[&>div]:bg-emerald-500' : '[&>div]:bg-red-500'}`}
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Compliance Alerts */}
      {complianceAlerts.length > 0 && (
        <Card className="border-amber-500/50 dark:border-amber-500/30 bg-amber-500/10 dark:bg-amber-500/20">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-5 w-5" />
              Compliance Alerts ({complianceAlerts.length} users below 80%)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {complianceAlerts.slice(0, 5).map((alert, i) => (
                <Badge key={i} variant="outline" className="bg-amber-100 dark:bg-amber-900/40 border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-300">
                  {alert.user_name}: {alert.compliance_rate}% ({alert.calls_without_notes} missing)
                </Badge>
              ))}
              {complianceAlerts.length > 5 && (
                <Badge variant="secondary">+{complianceAlerts.length - 5} more</Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="call-logs">Call Log Notes</TabsTrigger>
          <TabsTrigger value="by-user">By User</TabsTrigger>
          <TabsTrigger value="alerts">Alerts</TabsTrigger>
          <TabsTrigger value="qa-activity" className="flex items-center gap-1">
            <Shield className="h-4 w-4" />
            QA Activity
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-1">
            <BarChart3 className="h-4 w-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="resume-notes" className="flex items-center gap-1">
            <FileText className="h-4 w-4" />
            Resume Notes
          </TabsTrigger>
        </TabsList>

        {/* Call Logs Tab */}
        <TabsContent value="call-logs" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <Filter className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Filters:</span>
                </div>
                
                <Select value={filters.user_id} onValueChange={(v) => setFilters({...filters, user_id: v})}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users?.map(user => (
                      <SelectItem key={user.id} value={user.id}>{user.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={filters.location} onValueChange={(v) => setFilters({...filters, location: v})}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {locations.map(loc => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={filters.designation} onValueChange={(v) => setFilters({...filters, designation: v})}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="All Designations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Designations</SelectItem>
                    {designations.map(des => (
                      <SelectItem key={des} value={des}>{des}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={filters.has_notes} onValueChange={(v) => setFilters({...filters, has_notes: v})}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Notes Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="yes">With Notes</SelectItem>
                    <SelectItem value="no">Missing Notes</SelectItem>
                  </SelectContent>
                </Select>
                
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                  className="w-[150px]"
                />
                <span className="text-muted-foreground">to</span>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                  className="w-[150px]"
                />
                
                <Button variant="outline" size="sm" onClick={exportToCSV} className="ml-auto">
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Call Logs Table */}
          <Card>
            {/* Bulk Actions Toolbar */}
            {selectedLogs.length > 0 && (
              <div className="bg-blue-500/10 dark:bg-blue-500/20 border-b border-blue-400 dark:border-blue-600 p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                    {selectedLogs.length} call(s) selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-emerald-500 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10"
                    onClick={() => { setBulkAction('mark_compliant'); setShowBulkActionsDialog(true); }}
                  >
                    <CheckCircle2 className="h-4 w-4 mr-1" />
                    Mark Compliant
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-red-500 text-red-700 dark:text-red-400 hover:bg-red-500/10"
                    onClick={() => { setBulkAction('mark_non_compliant'); setShowBulkActionsDialog(true); }}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Mark Non-Compliant
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="border-blue-500 text-blue-700 dark:text-blue-400 hover:bg-blue-500/10"
                    onClick={() => { setBulkAction('review'); setShowBulkActionsDialog(true); }}
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Mark Reviewed
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => setSelectedLogs([])}
                  >
                    Clear Selection
                  </Button>
                </div>
              </div>
            )}
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox 
                        checked={selectedLogs.length === sortedCallLogs.length && sortedCallLogs.length > 0}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all"
                      />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Date/Time" sortKey="call_date" currentSort={sortConfig} onSort={handleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Created By" sortKey="log_created_by" currentSort={sortConfig} onSort={handleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Applicant" sortKey="applicant_name" currentSort={sortConfig} onSort={handleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Phone" sortKey="phone_number" currentSort={sortConfig} onSort={handleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Type" sortKey="call_type" currentSort={sortConfig} onSort={handleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Duration" sortKey="duration" currentSort={sortConfig} onSort={handleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Classification" sortKey="classification" currentSort={sortConfig} onSort={handleSort} />
                    </TableHead>
                    <TableHead>
                      <SortableHeader label="Alert" sortKey="alert_level" currentSort={sortConfig} onSort={handleSort} />
                    </TableHead>
                    <TableHead>Compliance Issues</TableHead>
                    <TableHead>Transcript</TableHead>
                    <TableHead>AI Analysis</TableHead>
                    <TableHead>
                      <SortableHeader label="Notes" sortKey="has_notes" currentSort={sortConfig} onSort={handleSort} />
                    </TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedCallLogs.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={14} className="text-center py-12 text-muted-foreground">
                        No call logs found. Click &quot;Sync from Calls&quot; to import call data.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedCallLogs.map((log) => (
                      <TableRow 
                        key={log.id} 
                        className={`${!log.has_notes && !log.manually_marked_compliant ? 'bg-red-500/10 dark:bg-red-500/20 hover:bg-red-500/20 dark:hover:bg-red-500/30' : 'hover:bg-muted/50'} ${selectedLogs.includes(log.id) ? 'bg-blue-500/10 dark:bg-blue-500/20' : ''}`}
                      >
                        <TableCell>
                          <Checkbox 
                            checked={selectedLogs.includes(log.id)}
                            onCheckedChange={() => handleSelectLog(log.id)}
                            aria-label={`Select row ${log.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="text-sm font-medium">
                              {new Date(log.call_date).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(log.call_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="font-medium">{log.log_created_by}</span>
                          </div>
                        </TableCell>
                        <TableCell>{log.applicant_name || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">{log.phone_number}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{log.call_type}</Badge>
                        </TableCell>
                        <TableCell>{log.duration || '-'}</TableCell>
                        <TableCell>
                          {log.classification ? (
                            <Badge className={`capitalize ${
                              log.classification === 'best' ? 'bg-emerald-500' :
                              log.classification === 'good' ? 'bg-blue-500' :
                              log.classification === 'improvement' ? 'bg-amber-500' :
                              'bg-red-500'
                            }`}>
                              {log.classification.replace('_', ' ')}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {log.alert_level ? (
                            <Badge className={`capitalize ${
                              log.alert_level === 'good' ? 'bg-emerald-500' :
                              log.alert_level === 'improvement' ? 'bg-yellow-500' :
                              log.alert_level === 'medium' ? 'bg-amber-500' :
                              'bg-red-500'
                            }`}>
                              {log.alert_level === 'critical' && <AlertTriangle className="h-3 w-3 mr-1" />}
                              {log.alert_level}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        {/* Compliance Issues Column */}
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-48">
                            {log.compliance_alerts && log.compliance_alerts.length > 0 ? (
                              log.compliance_alerts.slice(0, 3).map((alert, idx) => (
                                <Badge 
                                  key={idx} 
                                  variant="outline"
                                  className={`text-xs cursor-pointer ${
                                    alert.severity === 'critical' || alert.type?.includes('fatal') ? 'border-red-500 text-red-500 hover:bg-red-500/10' :
                                    alert.severity === 'high' ? 'border-orange-500 text-orange-500 hover:bg-orange-500/10' :
                                    'border-yellow-500 text-yellow-500 hover:bg-yellow-500/10'
                                  }`}
                                  onClick={() => setSelectedLog(log)}
                                  title={alert.description || alert.message || alert.type}
                                >
                                  {(alert.type || alert.category || 'Issue').substring(0, 15)}
                                </Badge>
                              ))
                            ) : !log.has_notes && !log.manually_marked_compliant ? (
                              <Badge variant="outline" className="text-xs border-red-500 text-red-500">
                                Missing Notes
                              </Badge>
                            ) : log.manually_marked_compliant ? (
                              <Badge variant="outline" className="text-xs border-emerald-500 text-emerald-500">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Cleared
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground text-xs">None</span>
                            )}
                            {log.compliance_alerts && log.compliance_alerts.length > 3 && (
                              <Badge variant="outline" className="text-xs cursor-pointer" onClick={() => setSelectedLog(log)}>
                                +{log.compliance_alerts.length - 3}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {log.has_transcript ? (
                            <Badge 
                              className="bg-blue-500 cursor-pointer hover:bg-blue-600"
                              onClick={() => setShowTranscript(log)}
                            >
                              <FileAudio className="h-3 w-3 mr-1" />
                              View
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {log.has_ai_analysis ? (
                            <Badge className="bg-purple-500">
                              <Brain className="h-3 w-3 mr-1" />
                              Yes
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {log.has_notes || log.manually_marked_compliant ? (
                            <Badge 
                              className="bg-emerald-500 cursor-pointer hover:bg-emerald-600"
                              onClick={() => setShowNotes(log)}
                            >
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              {log.manually_marked_compliant ? 'View' : 'View'}
                            </Badge>
                          ) : (
                            <Badge variant="destructive">
                              <XCircle className="h-3 w-3 mr-1" />
                              Missing
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => setSelectedLog(log)}
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => startEditingLog(log)}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4 text-blue-600" />
                            </Button>
                            {/* Quick Override Button */}
                            {(!log.has_notes && !log.manually_marked_compliant) && (
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-500/10"
                                onClick={() => handleSingleCompliance(log, true, 'Cleared by QA review')}
                                title="Clear Issue / Mark Compliant"
                              >
                                <CheckCircle2 className="h-4 w-4" />
                              </Button>
                            )}
                            {/* View Call Detail */}
                            {log.call_id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => window.open(`/dashboard/calls/${log.call_id}`, '_blank')}
                                title="Open Call Detail"
                              >
                                <ExternalLink className="h-4 w-4 text-purple-600" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* By User Tab */}
        <TabsContent value="by-user" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Compliance by User</CardTitle>
              <CardDescription>Call log notes compliance grouped by team member</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Total Calls</TableHead>
                    <TableHead>With Notes</TableHead>
                    <TableHead>Missing</TableHead>
                    <TableHead>Compliance</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {complianceByUser.map((user) => (
                    <TableRow key={user.user_id} className={user.compliance_rate < 80 ? 'bg-red-500/10 dark:bg-red-500/20' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{user.user_name}</p>
                            {user.designation && (
                              <p className="text-xs text-muted-foreground">{user.designation}</p>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{user.department || '-'}</TableCell>
                      <TableCell>
                        {user.location && (
                          <span className="flex items-center gap-1 text-sm">
                            <MapPin className="h-3 w-3" />
                            {user.location}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{user.total_calls}</TableCell>
                      <TableCell className="text-emerald-600">{user.calls_with_notes}</TableCell>
                      <TableCell className="text-red-600">{user.calls_without_notes}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress 
                            value={user.compliance_rate} 
                            className={`w-16 h-2 ${user.compliance_rate >= 80 ? '[&>div]:bg-emerald-500' : '[&>div]:bg-red-500'}`}
                          />
                          <span className={`font-medium ${user.compliance_rate >= 80 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {user.compliance_rate}%
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Compliance Alerts
              </CardTitle>
              <CardDescription>Users with less than 80% call log notes compliance</CardDescription>
            </CardHeader>
            <CardContent>
              {complianceAlerts.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle2 className="h-12 w-12 text-emerald-300 mx-auto mb-4" />
                  <p className="text-muted-foreground">All users are above 80% compliance!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {complianceAlerts.map((alert) => (
                    <div key={alert.user_id} className="p-4 bg-amber-500/10 dark:bg-amber-500/20 border border-amber-400 dark:border-amber-600 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                          <div>
                            <p className="font-medium">{alert.user_name}</p>
                            <p className="text-sm text-amber-700 dark:text-amber-400">{alert.message}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-300">
                          {alert.compliance_rate}% Compliance
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* QA Activity Tab */}
        <TabsContent value="qa-activity" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-500" />
                QA Activity & Performance
              </CardTitle>
              <CardDescription>
                Track quality assurance reviews, compliance overrides, and auditor performance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8">
                <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">Auditors Dashboard Available</h3>
                <p className="text-muted-foreground mb-4">
                  For full QA workflow management including alert reviews, compliance overrides, 
                  and detailed auditor performance tracking, visit the dedicated Auditors Dashboard.
                </p>
                <Button asChild>
                  <a href={`${window.location.pathname.replace('/reports', '/auditors-dashboard')}`}>
                    Open Auditors Dashboard
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <AnalyticsTab 
            callLogs={callLogs} 
            complianceByUser={complianceByUser}
            complianceStats={complianceStats}
            users={users}
            departments={departments}
          />
        </TabsContent>

        {/* Resume Notes Compliance Tab */}
        <TabsContent value="resume-notes" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Resume Notes Compliance
              </CardTitle>
              <CardDescription>
                Track if recruiters add notes when viewing applicant profiles in CEIPAL
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-12 space-y-4">
                <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="h-8 w-8 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-800">Coming Soon</h3>
                  <p className="text-muted-foreground max-w-md mx-auto mt-2">
                    This feature requires CEIPAL API access to applicant activity logs. We're working with CEIPAL to enable this integration.
                  </p>
                </div>
                
                <div className="mt-6 p-6 bg-muted/50 rounded-lg text-left max-w-lg mx-auto">
                  <h4 className="font-semibold mb-3">Planned Features:</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" />
                      <span>Track applicants viewed by each recruiter</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" />
                      <span>Verify if notes were added within a time window of viewing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" />
                      <span>Compliance scoring for note-taking habits</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" />
                      <span>View actual notes with timestamps for verification</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5" />
                      <span>Compare notes across team members</span>
                    </li>
                  </ul>
                </div>
                
                <div className="mt-4 p-4 bg-blue-500/10 dark:bg-blue-500/20 rounded-lg text-sm text-blue-700 dark:text-blue-300 max-w-lg mx-auto">
                  <strong>Technical Note:</strong> This feature requires CEIPAL's Activity Log or Audit Trail API, which may need to be requested from CEIPAL support. Standard APIs currently support Jobs, Submissions, Applicants, and Clients.
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Log Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Comprehensive Call Details
            </DialogTitle>
            <DialogDescription>
              {selectedLog && new Date(selectedLog.call_date).toLocaleString()}
            </DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-6">
              {/* Basic Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Created By</p>
                  <p className="font-medium">{selectedLog.log_created_by}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Call Type</p>
                  <Badge variant="outline" className="capitalize">{selectedLog.call_type}</Badge>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Applicant</p>
                  <p className="font-medium">{selectedLog.applicant_name || '-'}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Phone</p>
                  <p className="font-mono">{selectedLog.phone_number}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Duration</p>
                  <p>{selectedLog.duration || '-'}</p>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground">Call ID</p>
                  <p className="font-mono text-xs">{selectedLog.call_id || selectedLog.id}</p>
                </div>
              </div>

              {/* Status Badges */}
              <div className="flex flex-wrap gap-2 p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Notes:</span>
                  {selectedLog.has_notes || selectedLog.manually_marked_compliant ? (
                    <Badge className="bg-emerald-500">
                      <CheckCircle2 className="h-3 w-3 mr-1" />
                      {selectedLog.manually_marked_compliant ? 'Manually Compliant' : 'Compliant'}
                    </Badge>
                  ) : (
                    <Badge variant="destructive">
                      <XCircle className="h-3 w-3 mr-1" />
                      Non-Compliant
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Transcript:</span>
                  {selectedLog.has_transcript ? (
                    <Badge className="bg-blue-500">
                      <FileAudio className="h-3 w-3 mr-1" />
                      Available
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Not Available</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">AI Analysis:</span>
                  {selectedLog.has_ai_analysis ? (
                    <Badge className="bg-purple-500">
                      <Brain className="h-3 w-3 mr-1" />
                      Available
                    </Badge>
                  ) : (
                    <Badge variant="secondary">Not Available</Badge>
                  )}
                </div>
                {selectedLog.classification && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Classification:</span>
                    <Badge className={`capitalize ${
                      selectedLog.classification === 'best' ? 'bg-emerald-500' :
                      selectedLog.classification === 'good' ? 'bg-blue-500' :
                      selectedLog.classification === 'improvement' ? 'bg-amber-500' :
                      'bg-red-500'
                    }`}>
                      {selectedLog.classification.replace('_', ' ')}
                    </Badge>
                  </div>
                )}
                {selectedLog.alert_level && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">Alert:</span>
                    <Badge className={`capitalize ${
                      selectedLog.alert_level === 'good' ? 'bg-emerald-500' :
                      selectedLog.alert_level === 'improvement' ? 'bg-yellow-500' :
                      selectedLog.alert_level === 'medium' ? 'bg-amber-500' :
                      'bg-red-500'
                    }`}>
                      {selectedLog.alert_level === 'critical' && <AlertTriangle className="h-3 w-3 mr-1" />}
                      {selectedLog.alert_level}
                    </Badge>
                  </div>
                )}
              </div>
              
              {/* Notes Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    Call Notes
                  </p>
                  {selectedLog.notes_timestamp && (
                    <span className="text-xs text-muted-foreground">
                      Added: {new Date(selectedLog.notes_timestamp).toLocaleString()}
                    </span>
                  )}
                </div>
                <div className={`p-4 rounded-lg border ${selectedLog.has_notes || selectedLog.manually_marked_compliant ? 'bg-emerald-500/10 dark:bg-emerald-500/20 border-emerald-400 dark:border-emerald-600' : 'bg-red-500/10 dark:bg-red-500/20 border-red-400 dark:border-red-600'}`}>
                  {selectedLog.notes || selectedLog.description ? (
                    <p className="whitespace-pre-wrap">
                      {typeof (selectedLog.notes || selectedLog.description) === 'object' 
                        ? JSON.stringify(selectedLog.notes || selectedLog.description, null, 2)
                        : (selectedLog.notes || selectedLog.description)}
                    </p>
                  ) : (
                    <span className="text-red-600 dark:text-red-400 italic">No notes recorded - Compliance violation</span>
                  )}
                </div>
              </div>
              
              {/* Admin Notes */}
              {selectedLog.admin_notes && (
                <div>
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Edit className="h-4 w-4" />
                    Admin Notes
                  </p>
                  <div className="p-4 rounded-lg bg-blue-500/10 dark:bg-blue-500/20 border border-blue-400 dark:border-blue-600">
                    {typeof selectedLog.admin_notes === 'object' 
                      ? JSON.stringify(selectedLog.admin_notes, null, 2)
                      : selectedLog.admin_notes}
                  </div>
                </div>
              )}

              {/* Transcript Preview */}
              {selectedLog.has_transcript && selectedLog.transcript && (
                <div>
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <FileAudio className="h-4 w-4" />
                    Transcript Preview
                  </p>
                  <div className="p-4 rounded-lg bg-muted/50 dark:bg-muted/30 border border-border max-h-40 overflow-y-auto">
                    <p className="text-sm whitespace-pre-wrap">{selectedLog.transcript.substring(0, 500)}...</p>
                    {selectedLog.transcript.length > 500 && (
                      <Button variant="link" size="sm" className="mt-2 p-0" onClick={() => {setSelectedLog(null); setShowTranscript(selectedLog);}}>
                        View full transcript
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* AI Analysis Summary */}
              {selectedLog.has_ai_analysis && selectedLog.ai_feedback && (
                <div>
                  <p className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Brain className="h-4 w-4" />
                    AI Analysis Summary
                  </p>
                  <div className="p-4 rounded-lg bg-purple-500/10 dark:bg-purple-500/20 border border-purple-400 dark:border-purple-600">
                    <p className="text-sm">{typeof selectedLog.ai_feedback === 'object' ? selectedLog.ai_feedback.executive_assessment || selectedLog.ai_feedback.one_liner_verdict : selectedLog.ai_feedback}</p>
                    {selectedLog.average_score && (
                      <div className="mt-2 flex items-center gap-2">
                        <Star className="h-4 w-4 text-amber-500" />
                        <span className="font-medium">Score: {selectedLog.average_score}/10</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Quick Actions */}
              <div className="flex gap-2 pt-2 border-t">
                {selectedLog.call_id && (
                  <Button variant="outline" size="sm" onClick={() => window.open(`/calls/${selectedLog.call_id}`, '_blank')}>
                    <Eye className="h-4 w-4 mr-1" />
                    View Full Call
                  </Button>
                )}
                <Button variant="outline" size="sm" onClick={() => {setSelectedLog(null); startEditingLog(selectedLog);}}>
                  <Edit className="h-4 w-4 mr-1" />
                  Edit Log
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transcript Dialog */}
      <Dialog open={!!showTranscript} onOpenChange={() => setShowTranscript(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileAudio className="h-5 w-5" />
              Call Transcript
            </DialogTitle>
            <DialogDescription>
              {showTranscript && (
                <>
                  {showTranscript.applicant_name && <span>{showTranscript.applicant_name} • </span>}
                  {new Date(showTranscript.call_date).toLocaleString()}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {showTranscript && (
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>Created By: <strong>{showTranscript.log_created_by}</strong></span>
                <span>Duration: <strong>{showTranscript.duration || '-'}</strong></span>
                <span>Type: <Badge variant="outline">{showTranscript.call_type}</Badge></span>
              </div>
              <div className="p-4 rounded-lg bg-muted/50 border max-h-[50vh] overflow-y-auto">
                {showTranscript.transcript ? (
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">{showTranscript.transcript}</p>
                ) : (
                  <p className="text-muted-foreground italic">Transcript not available. The call may not have been processed yet.</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={!!showNotes} onOpenChange={() => setShowNotes(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Call Notes
            </DialogTitle>
            <DialogDescription>
              {showNotes && (
                <>
                  {showNotes.applicant_name && <span>{showNotes.applicant_name} • </span>}
                  {new Date(showNotes.call_date).toLocaleString()}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          {showNotes && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Created By: <strong>{showNotes.log_created_by}</strong></span>
                {showNotes.notes_timestamp && (
                  <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
                    Notes added: {new Date(showNotes.notes_timestamp).toLocaleString()}
                  </span>
                )}
              </div>
              
              {/* Main Notes */}
              <div>
                <p className="text-xs text-muted-foreground mb-1">Notes/Description</p>
                <div className={`p-4 rounded-lg border ${showNotes.has_notes || showNotes.manually_marked_compliant ? 'bg-emerald-500/10 dark:bg-emerald-500/20 border-emerald-400 dark:border-emerald-600' : 'bg-red-500/10 dark:bg-red-500/20 border-red-400 dark:border-red-600'}`}>
                  {showNotes.notes || showNotes.description ? (
                    <p className="whitespace-pre-wrap">
                      {typeof (showNotes.notes || showNotes.description) === 'object'
                        ? JSON.stringify(showNotes.notes || showNotes.description, null, 2)
                        : (showNotes.notes || showNotes.description)}
                    </p>
                  ) : (
                    <span className="text-red-600 dark:text-red-400 italic">No notes recorded</span>
                  )}
                </div>
              </div>
              
              {/* Compliance Status */}
              <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                <span className="text-sm">Compliance Status:</span>
                {showNotes.has_notes || showNotes.manually_marked_compliant ? (
                  <Badge className="bg-emerald-500">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    {showNotes.manually_marked_compliant ? 'Manually Marked Compliant' : 'Compliant'}
                  </Badge>
                ) : (
                  <Badge variant="destructive">
                    <XCircle className="h-3 w-3 mr-1" />
                    Non-Compliant - Missing Notes
                  </Badge>
                )}
              </div>
              
              {/* Admin Notes if any */}
              {showNotes.admin_notes && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Admin Notes</p>
                  <div className="p-3 rounded-lg bg-blue-500/10 dark:bg-blue-500/20 border border-blue-400 dark:border-blue-600">
                    {typeof showNotes.admin_notes === 'object'
                      ? JSON.stringify(showNotes.admin_notes, null, 2)
                      : showNotes.admin_notes}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Log Dialog */}
      <Dialog open={!!editingLog} onOpenChange={() => !isSaving && setEditingLog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" />
              Edit Call Log Compliance
            </DialogTitle>
            <DialogDescription>
              Update compliance status and classification for this call log.
              {editingLog && ` - ${editingLog.log_created_by} on ${new Date(editingLog.call_date).toLocaleDateString()}`}
            </DialogDescription>
          </DialogHeader>
          {editingLog && (
            <div className="space-y-4">
              {/* Mark as Compliant */}
              <div className="p-4 bg-muted/50 rounded-lg space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">Notes Status</p>
                    <p className="text-sm text-muted-foreground">
                      {editingLog.notes ? 'Original notes exist' : 'No original notes - API issue or missing'}
                    </p>
                  </div>
                  <Select 
                    value={editingLog.has_notes ? 'compliant' : 'non-compliant'} 
                    onValueChange={(v) => setEditingLog({
                      ...editingLog, 
                      has_notes: v === 'compliant',
                      manually_marked_compliant: v === 'compliant' && !editingLog.notes
                    })}
                  >
                    <SelectTrigger className="w-[160px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="compliant">
                        <span className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                          Compliant
                        </span>
                      </SelectItem>
                      <SelectItem value="non-compliant">
                        <span className="flex items-center gap-2">
                          <XCircle className="h-4 w-4 text-red-500" />
                          Non-Compliant
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Classification */}
              <div>
                <label className="text-sm font-medium">Classification</label>
                <Select 
                  value={editingLog.classification || ''} 
                  onValueChange={(v) => setEditingLog({...editingLog, classification: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select classification" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLASSIFICATIONS.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        <span className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${c.color}`}></span>
                          {c.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Alert Level */}
              <div>
                <label className="text-sm font-medium">Alert Level</label>
                <Select 
                  value={editingLog.alert_level || ''} 
                  onValueChange={(v) => setEditingLog({...editingLog, alert_level: v})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select alert level" />
                  </SelectTrigger>
                  <SelectContent>
                    {ALERT_LEVELS.map(a => (
                      <SelectItem key={a.value} value={a.value}>
                        <span className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${a.color}`}></span>
                          {a.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Admin Notes */}
              <div>
                <label className="text-sm font-medium">Admin Notes (Reason for Override)</label>
                <Input
                  className="mt-1"
                  placeholder="e.g., Notes exist in CEIPAL but API didn't sync properly"
                  value={editingLog.admin_notes || ''}
                  onChange={(e) => setEditingLog({...editingLog, admin_notes: e.target.value})}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Add a reason when manually marking as compliant
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditingLog(null)} disabled={isSaving}>
                  Cancel
                </Button>
                <Button onClick={handleSaveEdit} disabled={isSaving}>
                  {isSaving ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving...</>
                  ) : (
                    <>Save Changes</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Actions Dialog */}
      <Dialog open={showBulkActionsDialog} onOpenChange={setShowBulkActionsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {bulkAction === 'mark_compliant' && <CheckCircle2 className="h-5 w-5 text-emerald-500" />}
              {bulkAction === 'mark_non_compliant' && <XCircle className="h-5 w-5 text-red-500" />}
              {bulkAction === 'review' && <Check className="h-5 w-5 text-blue-500" />}
              {bulkAction === 'mark_compliant' && 'Mark as Compliant'}
              {bulkAction === 'mark_non_compliant' && 'Mark as Non-Compliant'}
              {bulkAction === 'review' && 'Mark as Reviewed'}
            </DialogTitle>
            <DialogDescription>
              {selectedLogs.length} call(s) will be updated
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-muted/50 rounded-lg p-3 text-sm">
              <p className="font-medium mb-2">Selected Calls:</p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {sortedCallLogs.filter(log => selectedLogs.includes(log.id)).slice(0, 5).map(log => (
                  <div key={log.id} className="flex items-center gap-2 text-xs">
                    <span className="font-mono">{log.phone_number || 'N/A'}</span>
                    <span className="text-muted-foreground">-</span>
                    <span>{new Date(log.call_date).toLocaleDateString()}</span>
                  </div>
                ))}
                {selectedLogs.length > 5 && (
                  <p className="text-xs text-muted-foreground">...and {selectedLogs.length - 5} more</p>
                )}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="bulk-qa-notes">QA Notes (Optional)</Label>
              <Textarea
                id="bulk-qa-notes"
                placeholder="Add notes for this bulk action..."
                value={bulkQaNotes}
                onChange={(e) => setBulkQaNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBulkActionsDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkAction}
              disabled={isBulkProcessing}
              className={
                bulkAction === 'mark_compliant' ? 'bg-emerald-600 hover:bg-emerald-700' :
                bulkAction === 'mark_non_compliant' ? 'bg-red-600 hover:bg-red-700' :
                'bg-blue-600 hover:bg-blue-700'
              }
            >
              {isBulkProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {bulkAction === 'mark_compliant' && 'Mark Compliant'}
                  {bulkAction === 'mark_non_compliant' && 'Mark Non-Compliant'}
                  {bulkAction === 'review' && 'Mark Reviewed'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
